// src/components/sensors/sensorLiveStatus.js
// FlowSense sensor tables — shared live-status model — NEW FILE
// ═══════════════════════════════════════════════════════════════════════════
// Pure helpers (no React) used by ReadingTable and WaterLevelTable.
//
// Why this exists
//   Both tables marked a sensor "online" only while the dashboard WebSocket
//   was connected AND the reading timestamp was < 5 min old. Any WebSocket
//   blip therefore turned the whole fleet "offline".
//
// Model
//   lastHeardMs = newest of
//     • the moment this browser received a new reading that was measured within
//       onlineMs (tracked by useLiveSensorRegistry),
//     • reading.received_at / reading.timestamp (future values are clamped
//       to "now" and flagged as clock skew),
//     • sensor.last_activity_at / last_seen_at / latest_reading.timestamp
//       (sent by GET /api/v1/sensors).
//   State from the age of lastHeardMs:
//     transmitting ≤ transmittingMs < online ≤ onlineMs < delayed ≤ offlineMs < offline
//     no_data      = nothing has ever been heard
//   isOnline keeps the previous 5-minute meaning (transmitting | online | delayed),
//   so the existing Online / Offline tabs and counts behave the same.
//   The WebSocket link state is reported separately (LiveFeedBanner) and never
//   changes a sensor's own status.
// ═══════════════════════════════════════════════════════════════════════════

// The CaribTech nodes post every 15 s, so 30 s = "a reading just landed",
// 2 min = up to ~7 missed posts, 5 min = the TTL the tables already used.
export const DEFAULT_LIVE_THRESHOLDS = Object.freeze({
  transmittingMs: 30 * 1000,
  onlineMs: 2 * 60 * 1000,
  offlineMs: 5 * 60 * 1000,
  // Sensors that drop out of the `sensors` prop (e.g. while the page re-fetches)
  // stay on screen this long before they are removed.
  retainMs: 2 * 60 * 1000,
  // Device timestamps further than this in the future are treated as clock skew.
  futureSkewMs: 2 * 60 * 1000,
});

export const LIVE_STATE = Object.freeze({
  TRANSMITTING: 'transmitting',
  ONLINE: 'online',
  DELAYED: 'delayed',
  OFFLINE: 'offline',
  NO_DATA: 'no_data',
});

export const LIVE_STATE_META = Object.freeze({
  transmitting: { label: 'Transmitting', short: 'LIVE', color: 'green', rank: 4, isOnline: true },
  online: { label: 'Online', short: 'ONLINE', color: 'green', rank: 3, isOnline: true },
  delayed: { label: 'Delayed', short: 'DELAYED', color: 'yellow', rank: 2, isOnline: true },
  offline: { label: 'Offline', short: 'OFFLINE', color: 'red', rank: 1, isOnline: false },
  no_data: { label: 'No data', short: 'NO DATA', color: 'gray', rank: 0, isOnline: false },
});

export const mergeThresholds = (overrides) =>
  overrides ? { ...DEFAULT_LIVE_THRESHOLDS, ...overrides } : DEFAULT_LIVE_THRESHOLDS;

/** Case/whitespace-insensitive device key ("uno-r4-01 " === "UNO-R4-01"). */
export const normalizeId = (id) => (id === null || id === undefined ? '' : String(id).trim().toUpperCase());

/** Original device id of a sensor record, whatever field the API used. */
export const sensorIdOf = (s) =>
  s ? s.device_id ?? s.deviceId ?? s.sensor_id ?? s.id ?? null : null;

// "2026-10-31 13:05:00" (MySQL DATETIME without zone). The nodes send UTC
// ("…Z") and the API stores it as received, so a zone-less value is UTC.
const ZONELESS_RE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * Any timestamp → epoch ms, or null.
 * Accepts Date, epoch ms, epoch seconds, ISO strings and zone-less MySQL strings.
 */
export const toMillis = (ts) => {
  if (ts === null || ts === undefined || ts === '') return null;
  if (ts instanceof Date) {
    const t = ts.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof ts === 'number' || (typeof ts === 'string' && /^\d{9,13}(\.\d+)?$/.test(ts.trim()))) {
    const n = Number(ts);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n < 1e12 ? Math.round(n * 1000) : Math.round(n); // seconds vs ms
  }
  if (typeof ts !== 'string') return null;
  const s = ts.trim();
  const t = Date.parse(ZONELESS_RE.test(s) ? `${s.replace(' ', 'T')}Z` : s);
  return Number.isFinite(t) ? t : null;
};

const maxOf = (...values) => {
  let best = null;
  for (const v of values) if (v !== null && v !== undefined && Number.isFinite(v) && (best === null || v > best)) best = v;
  return best;
};

/** When the reading was measured / received according to its own fields. */
export const readingTimeMs = (r) => (r ? maxOf(toMillis(r.received_at), toMillis(r.timestamp), toMillis(r.time)) : null);

/** Last activity the REST API reported for a sensor record. */
export const sensorActivityMs = (s) =>
  s ? maxOf(
    toMillis(s.last_activity_at),
    toMillis(s.last_seen_at),
    toMillis(s.latest_reading?.timestamp),
    toMillis(s.last_reading_at),
  ) : null;

/** Value + time fingerprint: a change means a different reading. */
export const readingSignature = (r) => {
  if (!r || typeof r !== 'object') return '';
  const value = r.psi ?? r.pressure ?? r.value ?? r.level ?? r.water_level ?? '';
  return `${r.timestamp ?? r.received_at ?? ''}|${value}`;
};

export const resolveLiveState = (lastHeardMs, now, thresholds = DEFAULT_LIVE_THRESHOLDS) => {
  if (lastHeardMs === null || lastHeardMs === undefined || !Number.isFinite(lastHeardMs)) return LIVE_STATE.NO_DATA;
  const age = Math.max(0, now - lastHeardMs);
  if (age <= thresholds.transmittingMs) return LIVE_STATE.TRANSMITTING;
  if (age <= thresholds.onlineMs) return LIVE_STATE.ONLINE;
  if (age <= thresholds.offlineMs) return LIVE_STATE.DELAYED;
  return LIVE_STATE.OFFLINE;
};

/** Compact relative age that changes at most every 10 s (keeps memoised rows quiet). */
export const formatAgo = (ms, now = Date.now()) => {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return null;
  const s = Math.max(0, Math.floor((now - ms) / 1000));
  if (s < 10) return 'just now';
  if (s < 60) return `${Math.floor(s / 10) * 10}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const formatDurationShort = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} d`;
};

/** Tooltip text for a live state, with the thresholds that produced it. */
export const describeLiveState = (row, thresholds = DEFAULT_LIVE_THRESHOLDS, now = Date.now()) => {
  const meta = LIVE_STATE_META[row.liveState] || LIVE_STATE_META.no_data;
  if (row.liveState === LIVE_STATE.NO_DATA) return 'No reading has been received from this sensor yet.';
  const age = formatAgo(row.lastHeardMs, now);
  const parts = [`${meta.label} · last data ${age}`];
  if (row.liveState === LIVE_STATE.TRANSMITTING) parts.push(`(reading within ${formatDurationShort(thresholds.transmittingMs)})`);
  if (row.liveState === LIVE_STATE.DELAYED) parts.push(`(no reading for over ${formatDurationShort(thresholds.onlineMs)})`);
  if (row.liveState === LIVE_STATE.OFFLINE) parts.push(`(no reading for over ${formatDurationShort(thresholds.offlineMs)})`);
  if (row.clockSkew) parts.push('· device clock is ahead of server time');
  if (row.measuredMs && row.lastHeardMs && row.lastHeardMs - row.measuredMs > 30000) {
    parts.push(`· it was measured ${formatAgo(row.measuredMs, now)} (delivered late)`);
  }
  return parts.join(' ');
};
